import { useCallback, useEffect, useState } from 'react'
import { KpiTile } from '../components/KpiTile.js'
import { fetchJobs, ignoreDealSync, retryDealSync, runJob, type JobDescriptor, type JobRunResult, type JobsStatus } from '../lib/api.js'
import { formatUKDate } from '@shared/dates'

/**
 * Admin → Scheduled jobs. Every background job in the registry, with a manual
 * trigger and its run history.
 *
 * The list is driven entirely by what the server registers, so a new scheduled
 * job appears here with no change to this file. That's deliberate: Netlify can't
 * serve scheduled functions over HTTP, so a job without a manual trigger is a
 * job nobody can test until 2am — and the way to stop that recurring is to make
 * the trigger automatic rather than remembered.
 */

/** Decision 184: a run's persisted warnings (older rows have none). */
function warnsOf(run: Record<string, unknown>): string[] {
  const w = run.warnings
  return Array.isArray(w) ? w.map((x) => String(x)) : []
}

function when(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  return `${formatUKDate(d)} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

/**
 * Decision 199 (Mark, 2026-09-05): the page had grown into a flat wall of
 * thirteen cards, most of them one-offs used to build the data. The registry
 * now says where each job sits (`group`); the page draws three collapsible
 * sections — scheduled · day-to-day tools · setup & one-off heals — with the
 * one-offs collapsed by default. A collapsed section still shows how many
 * jobs it holds and whether any last run failed, and a section with a job
 * running in the background opens itself so the poll is never hidden.
 */
const JOB_GROUPS: Array<{ id: 'scheduled' | 'tools' | 'oneoff'; title: string; blurb: string; openByDefault: boolean }> = [
  { id: 'scheduled', title: 'Scheduled', blurb: 'Run on a timer by Netlify. Run now kicks the same code by hand.', openByDefault: true },
  { id: 'tools', title: 'Day-to-day tools', blurb: 'Manual work done from time to time — imports, rosters, audits.', openByDefault: true },
  { id: 'oneoff', title: 'Setup & one-off heals', blurb: 'Run once at setup, or to correct historical data after a fix. All safe to rerun; read the dry run first.', openByDefault: false },
]
const OPEN_KEY = 'w9.jobs.sections'

export function ScheduledJobs() {
  const [status, setStatus] = useState<JobsStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [running, setRunning] = useState<string | null>(null)
  const [results, setResults] = useState<Record<string, JobRunResult>>({})
  // Upload jobs (e.g. the Quotient recipients import): the chosen file's text,
  // per job key. Held in memory only — it is sent as the run's payload.
  const [uploads, setUploads] = useState<Record<string, { name: string; text: string }>>({})
  // Decision 184: the queued row currently being pushed due.
  const [retrying, setRetrying] = useState<string | null>(null)
  // Decision 199: the three sections' open/collapsed state — remembered per
  // browser (a convenience, never load-bearing: a failed read = the defaults).
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const d = Object.fromEntries(JOB_GROUPS.map((g) => [g.id, g.openByDefault]))
    try {
      const saved = JSON.parse(localStorage.getItem(OPEN_KEY) ?? 'null') as Record<string, boolean> | null
      return saved && typeof saved === 'object' ? { ...d, ...saved } : d
    } catch { return d }
  })
  const toggle = (id: string) => {
    const next = { ...open, [id]: !open[id] }
    setOpen(next)
    try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)) } catch { /* private mode etc. */ }
  }

  const load = useCallback(() => {
    fetchJobs().then(setStatus).catch((e: Error) => setError(e.message))
  }, [])
  useEffect(load, [load])

  const pickFile = async (key: string, file: File | null) => {
    if (!file) return
    const text = await file.text()
    setUploads((u) => ({ ...u, [key]: { name: file.name, text } }))
  }

  // Decision 194: a background run answers "started"; the result is the run
  // row it writes when it finishes. Poll the history until a run for this
  // job newer than the hand-off appears (cap ~5 min), then show THAT.
  const [waiting, setWaiting] = useState<Record<string, string>>({})   // key → ISO of the hand-off
  useEffect(() => {
    const keys = Object.keys(waiting)
    if (keys.length === 0) return
    const t = setInterval(() => {
      fetchJobs().then((st) => {
        setStatus(st)
        for (const key of keys) {
          const since = new Date(waiting[key]).getTime()
          const run = st.runs.find((r) => String(r.job ?? '').split(':')[0] === key && new Date(String(r.started_at)).getTime() >= since - 60_000 && r.finished_at)
          const expired = Date.now() - since > 5 * 60_000
          if (run || expired) {
            setWaiting((w) => { const n = { ...w }; delete n[key]; return n })
            setResults((r) => ({
              ...r,
              [key]: run
                ? {
                    ok: run.ok !== false, dryRun: Boolean(run.dry_run),
                    counts: Object.fromEntries(Object.entries((run.counts as Record<string, unknown>) ?? {}).filter(([, v]) => typeof v === 'number')) as Record<string, number>,
                    warnings: warnsOf(run), error: run.error ? String(run.error) : undefined,
                    message: `Finished in the background at ${when(run.finished_at)}.`,
                  }
                : { ok: false, dryRun: false, counts: {}, warnings: [], error: 'No finished run appeared within 5 minutes — check the history below and the Netlify function log for admin-jobs-background.' },
            }))
          }
        }
      }).catch(() => {})
    }, 5000)
    return () => clearInterval(t)
  }, [waiting])

  const go = async (key: string, dryRun: boolean, needsUpload = false) => {
    if (needsUpload && !uploads[key]) {
      setError('Choose the file first — this job runs against an uploaded export.')
      return
    }
    setRunning(`${key}:${dryRun}`)
    setError(null)
    try {
      const res = await runJob(key, dryRun, needsUpload ? uploads[key].text : undefined)
      setResults((r) => ({ ...r, [key]: res }))
      if (res.started) setWaiting((w) => ({ ...w, [key]: new Date().toISOString() }))
      load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setRunning(null)
    }
  }

  if (error && !status) return <p className="pill pill--stop">{error}</p>
  if (!status) return <p className="muted">Loading jobs…</p>

  const renderJob = (job: JobDescriptor) => {
    const result = results[job.key]
    return (
          <div className="card" key={job.key}>
            <div className="card__head">
              <div>
                <h2>{job.label}</h2>
                <p className="muted small" style={{ margin: '4px 0 0', maxWidth: '72ch' }}>{job.description}</p>
              </div>
              <div className="toolbar">
                <span className="pill">{job.schedule ?? 'Manual only'}</span>
                {job.background && <span className="pill pill--info" title="A real run goes to a background function — the button answers at once and the run appears in the history when it finishes">runs in background</span>}
                {job.upload && (
                  <label className="btn" style={{ cursor: 'pointer' }} title={job.upload.label}>
                    {uploads[job.key] ? uploads[job.key].name : `Choose ${job.upload.label}…`}
                    <input
                      type="file"
                      accept={job.upload.accept}
                      style={{ display: 'none' }}
                      onChange={(e) => void pickFile(job.key, e.target.files?.[0] ?? null)}
                    />
                  </label>
                )}
                {job.supportsDryRun && (
                  <button
                    className="btn"
                    onClick={() => go(job.key, true, Boolean(job.upload))}
                    disabled={!status.canRun || running !== null || (Boolean(job.upload) && !uploads[job.key])}
                    title={job.dryRunMeans}
                  >
                    {running === `${job.key}:true` ? 'Running…' : 'Dry run'}
                  </button>
                )}
                <button
                  className="btn btn--primary"
                  onClick={() => go(job.key, false, Boolean(job.upload))}
                  disabled={!status.canRun || running !== null || (Boolean(job.upload) && !uploads[job.key])}
                >
                  {running === `${job.key}:false` ? 'Running…' : 'Run now'}
                </button>
              </div>
            </div>

            <div className="card__body">
              <dl className="kv" style={{ marginBottom: result ? 16 : 0 }}>
                <dt>Last run</dt>
                <dd>
                  {job.lastRun ? (
                    <>
                      {when((job.lastRun as Record<string, unknown>).started_at)}{' '}
                      {(job.lastRun as Record<string, unknown>).ok
                        ? <span className="pill pill--ok">ok</span>
                        : <span className="pill pill--stop">failed</span>}
                      {(job.lastRun as Record<string, unknown>).dry_run ? <span className="pill" style={{ marginLeft: 6 }}>dry run</span> : null}
                    </>
                  ) : <span className="muted">never</span>}
                </dd>
              </dl>

              {result && (
                <>
                  <h3 style={{ marginBottom: 8 }}>
                    {result.dryRun ? 'Dry run' : 'Run'}{' '}
                    {result.started && waiting[job.key]
                      ? <span className="pill pill--info">running in the background…</span>
                      : result.ok ? <span className="pill pill--ok">completed</span> : <span className="pill pill--stop">failed</span>}
                    {result.dryRun && result.ok && <span className="muted small" style={{ marginLeft: 8, fontWeight: 400 }}>Nothing was written.</span>}
                  </h3>

                  {result.error && <p className="pill pill--stop" style={{ display: 'block', marginBottom: 12 }}>{result.error}</p>}
                  {result.message && <p className="small">{result.message}</p>}

                  <dl className="kv">
                    {Object.entries(result.counts).map(([k, v]) => (
                      <>
                        <dt key={k} style={{ textTransform: 'capitalize' }}>{k.replace(/([A-Z])/g, ' $1').toLowerCase()}</dt>
                        <dd key={`${k}-v`}>{v}</dd>
                      </>
                    ))}
                  </dl>

                  {result.warnings.length > 0 && (
                    <ul className="small" style={{ margin: '14px 0 0', paddingLeft: 18, lineHeight: 1.8 }}>
                      {result.warnings.map((w) => <li key={w}>{w}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
    )
  }


  return (
    <>
      <div className="page-head">
        <div>
          <h1>Scheduled jobs</h1>
          <p>
            Background work, and the button to run it now. Netlify won't serve a scheduled function
            over HTTP, so every job is registered once and gets both a schedule and a manual trigger —
            the same code either way.
          </p>
        </div>
      </div>

      <div className="tiles">
        <KpiTile accent label="Products cached" value={String(status.catalogue.active)} sub={`${status.catalogue.total} including withdrawn`} />
        <KpiTile label="Last catalogue sync" value={status.catalogue.lastSyncedAt ? when(status.catalogue.lastSyncedAt).split(' ')[0] : '—'} sub={status.catalogue.lastSyncedAt ? when(status.catalogue.lastSyncedAt).split(' ')[1] : 'never run'} />
        <KpiTile label="Jobs registered" value={String(status.jobs.length)} />
      </div>

      {error && <p className="pill pill--stop" style={{ display: 'block', marginBottom: 16 }}>{error}</p>}

      {/* Decision 194: the scheduler verdict — judged on SCHEDULED runs only,
          so a manual run cannot hide a dead schedule. */}
      {status.schedule && (
        <div className={`bbanner ${status.schedule.stale ? '' : 'bbanner--accepted'}`} data-testid="schedule-verdict" style={{ marginBottom: 16, borderRadius: 10, border: '1px solid' }}>
          {status.schedule.stale ? '⛔' : '✓'} <span>
            {status.schedule.stale
              ? <>
                  <strong>The deal-sync schedule has stopped.</strong> Netlify last invoked it {status.schedule.ageMinutes} min ago
                  ({status.schedule.lastScheduledAt ? when(status.schedule.lastScheduledAt) : '—'}).
                  {status.schedule.lastKickAt ? <> The portal kicked a background sweep at {when(status.schedule.lastKickAt)} and will keep doing so every 20 minutes.</> : null}
                  {status.schedule.lastNotifiedAt ? <> Admin mailbox notified {when(status.schedule.lastNotifiedAt)}.</> : null}
                  {' '}<b>Fix:</b> Netlify → Deploys → Trigger deploy → Clear cache and deploy site; if the next 10-minute tick still does not fire, raise it with Netlify support.
                </>
              : <>
                  <strong>Scheduler alive</strong> — the deal-sync sweep last ran on schedule {status.schedule.ageMinutes == null ? 'never (no scheduled run recorded yet)' : `${status.schedule.ageMinutes} min ago`}.
                  {' '}<span className="muted">The portal checks this on every staff sign-in, every health fetch and nightly; a stopped schedule is kicked in the background and emailed to the admin mailbox.</span>
                </>}
          </span>
        </div>
      )}

      {JOB_GROUPS.map((g) => {
        const jobs = status.jobs.filter((j) => (j.group ?? 'tools') === g.id)
        if (jobs.length === 0) return null
        const failed = jobs.filter((j) => j.lastRun && !(j.lastRun as Record<string, unknown>).ok).length
        // A job running in the background must stay visible while the page polls for its run.
        const busy = jobs.some((j) => waiting[j.key] || running?.startsWith(`${j.key}:`))
        const isOpen = Boolean(open[g.id]) || busy
        return (
          <section key={g.id} data-testid={`jobs-${g.id}`} style={{ marginBottom: 18 }}>
            <div className="card" style={{ marginBottom: isOpen ? 10 : 0 }}>
              <div className="card__head" style={{ cursor: 'pointer', userSelect: 'none' }}
                role="button" aria-expanded={isOpen}
                onClick={() => toggle(g.id)}>
                <div>
                  <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span aria-hidden style={{ display: 'inline-block', width: 14, transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▸</span>
                    {g.title}
                  </h2>
                  <p className="muted small" style={{ margin: '4px 0 0 22px' }}>{g.blurb}</p>
                </div>
                <div className="toolbar">
                  {failed > 0 && <span className="pill pill--stop">{failed} failed</span>}
                  {busy && <span className="pill pill--info">running…</span>}
                  <span className="tiny muted">{jobs.length} job{jobs.length === 1 ? '' : 's'}{isOpen ? '' : ` · ${jobs.map((j) => j.label).join(' · ')}`}</span>
                </div>
              </div>
            </div>
            {isOpen && jobs.map(renderJob)}
          </section>
        )
      })}

      {/* Decision 184: the deal-sync queue itself. `last_error` has been stored
          on every row since migration 013 and nothing ever read it — the only
          trace of a failing sync was a count on the System status page. */}
      {status.queue && (
        <div className="card">
          <div className="card__head">
            <h2>Deal sync queue</h2>
            <span className="muted small">
              {status.queue.total} queued
              {status.queue.rows.filter((r) => r.attempts >= 5).length > 0
                && ` · ${status.queue.rows.filter((r) => r.attempts >= 5).length} retried 5+ times`}
            </span>
          </div>
          <div className="card__body">
            {status.queue.total === 0 && (
              <p className="muted small">
                Nothing queued — every accepted, sent or closed quote has reached its HubSpot deal.
              </p>
            )}
            {status.queue.total > 0 && (
              <p className="muted small" style={{ marginTop: 0 }}>
                <b>Retry now</b> makes a row due for the next sweep. <b>Ignore</b> stops retrying one that never
                can succeed — a deal deleted in HubSpot, say — and records why on the quote's activity trail.
                A deleted deal behind a <i>closed</i> quote now clears itself.
              </p>
            )}
            {status.queue.truncated && (
              <p className="pill pill--warn" style={{ marginBottom: 8 }}>
                Showing the first {status.queue.rows.length} of {status.queue.total} — most-retried first.
              </p>
            )}
          </div>
          {status.queue.total > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Quote</th><th>Queued because</th><th>Tries</th>
                    <th>Next attempt</th><th>Last error</th><th />
                  </tr>
                </thead>
                <tbody>
                  {status.queue.rows.map((r) => (
                    <tr key={r.quoteId}>
                      <td>
                        <strong>{r.quoteNumber}</strong>
                        <div className="tiny muted">{r.partnerName} · {r.status}</div>
                      </td>
                      <td className="small muted">{r.reason}</td>
                      <td>
                        {r.attempts >= 5
                          ? <span className="pill pill--stop" title="Failing repeatedly — read the error.">{r.attempts}</span>
                          : <span className="muted">{r.attempts}</span>}
                      </td>
                      <td className="small muted">{when(r.nextAttemptAt)}</td>
                      <td className="small" style={{ maxWidth: 420 }}>
                        {r.lastError
                          ? <span title={r.lastError}>{r.lastError.slice(0, 200)}{r.lastError.length > 200 ? '…' : ''}</span>
                          : <span className="muted">— not attempted yet</span>}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn--sm" disabled={retrying === r.quoteId}
                          title="Make it due now — the next sweep picks it up. The try count is kept."
                          onClick={() => {
                            setRetrying(r.quoteId)
                            retryDealSync(r.quoteId)
                              .then(load)
                              .catch((e: Error) => setError(e.message))
                              .finally(() => setRetrying(null))
                          }}>
                          {retrying === r.quoteId ? 'Queuing…' : 'Retry now'}
                        </button>{' '}
                        {/* Decision 185: some failures can never succeed — a deal
                            deleted in HubSpot behind a closed quote is the case
                            that prompted this. Stop retrying, and say why on the
                            quote's trail. */}
                        <button className="btn btn--sm" disabled={retrying === r.quoteId}
                          title="Stop retrying this one. It leaves the queue; the reason is recorded on the quote's activity trail."
                          onClick={() => {
                            const reason = prompt(
                              `Stop retrying ${r.quoteNumber}?\n\n`
                              + `Last error: ${r.lastError ?? '(none recorded)'}\n\n`
                              + 'Why is it safe to ignore? (recorded on the quote)',
                            )
                            if (reason === null) return
                            setRetrying(r.quoteId)
                            ignoreDealSync(r.quoteId, reason.trim())
                              .then(load)
                              .catch((e: Error) => setError(e.message))
                              .finally(() => setRetrying(null))
                          }}>
                          Ignore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <h2>Recent runs</h2>
          <span className="muted small">{status.runs.length} shown</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Started</th><th>Job</th><th>Trigger</th><th>Result</th><th>Counts</th></tr>
            </thead>
            <tbody>
              {status.runs.map((r, i) => {
                const [key, trigger] = String(r.job ?? '').split(':')
                const counts = (r.counts ?? {}) as Record<string, unknown>
                return (
                  <tr key={i}>
                    <td className="muted">{when(r.started_at)}</td>
                    <td className="small">{key}{r.dry_run ? <span className="pill" style={{ marginLeft: 6 }}>dry run</span> : null}</td>
                    <td className="muted small">{trigger ?? '—'}</td>
                    <td>{r.ok ? <span className="pill pill--ok">ok</span> : <span className="pill pill--stop" title={String(r.error ?? '')}>failed</span>}</td>
                    <td className="small muted">
                      {Object.entries(counts)
                        .filter(([k, v]) => k !== 'warnings' && typeof v === 'number' && v !== 0)
                        .map(([k, v]) => `${k} ${v}`)
                        .join(' · ') || '—'}
                      {/* Decision 184: warnings persist now, so a SCHEDULED
                          run's "this quote failed" lines survive to be read. */}
                      {warnsOf(r).length > 0 && (
                        <div className="tiny" style={{ marginTop: 3 }}>
                          {warnsOf(r).slice(0, 3).map((w, n) => <div key={n}>⚠️ {w}</div>)}
                          {warnsOf(r).length > 3 && <div className="muted">…and {warnsOf(r).length - 3} more</div>}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
              {status.runs.length === 0 && (
                <tr><td colSpan={5} className="muted" style={{ padding: 20 }}>Nothing has run yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
